import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  LayoutDashboard, Building2, Receipt, Wallet, Mail as MailIcon,
  TrendingUp, PieChart, Bell, LogOut, Zap, CalendarClock, CheckCheck,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Genel Bakış", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/companies", label: "Cari Hesaplar", icon: Building2, testid: "nav-companies" },
  { to: "/payments", label: "Ödemeler & Tahsilatlar", icon: CalendarClock, testid: "nav-payments" },
  { to: "/invoices", label: "Faturalar", icon: Receipt, testid: "nav-invoices" },
  { to: "/income-expense", label: "Gelir / Gider", icon: TrendingUp, testid: "nav-income-expense" },
  { to: "/banks", label: "Banka Hesapları", icon: Wallet, testid: "nav-banks" },
  { to: "/reports", label: "Finansal Raporlar", icon: PieChart, testid: "nav-reports" },
  { to: "/bulk-mail", label: "Toplu Mail", icon: MailIcon, testid: "nav-bulk-mail" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);

  async function loadNotifs() {
    try {
      const { data } = await api.get("/notifications");
      setNotifs(data);
    } catch (e) {
      // Silent: notifications are non-critical; log once for debugging
      console.warn("Bildirimler yüklenemedi", e?.message);
    }
  }
  useEffect(() => {
    loadNotifs();
    const t = setInterval(loadNotifs, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const unread = notifs.filter((n) => !n.read).length;

  async function markAllRead() {
    await api.post("/notifications/mark-all-read");
    await loadNotifs();
  }

  async function doLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <div className="min-h-screen flex bg-[#090D14]">
      {/* Sidebar */}
      <aside className="w-64 border-r border-white/5 bg-[#0B111C] hidden lg:flex flex-col fixed inset-y-0 left-0">
        <Link to="/" className="flex items-center gap-3 px-6 py-5 border-b border-white/5">
          <div className="w-9 h-9 rounded-lg bg-amber-500/15 border border-amber-500/30 grid place-items-center">
            <Zap className="w-5 h-5 text-amber-400" />
          </div>
          <div className="leading-tight">
            <div className="font-bold tracking-tight text-white text-[15px]">AVES ELEKTRİK</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400/70 mono">Otomasyon · Finans</div>
          </div>
        </Link>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all ${
                  isActive
                    ? "bg-amber-500/10 text-amber-300 border border-amber-500/20"
                    : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              <span>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 grid place-items-center font-bold text-black">
              {(user?.name || "A")[0].toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="text-sm text-white truncate" data-testid="current-user-name">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate">{user?.email}</div>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={doLogout}
            className="w-full border-white/10 hover:bg-red-500/10 hover:text-red-300 hover:border-red-500/30"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Çıkış Yap
          </Button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        {/* Top bar with notifications */}
        <div className="sticky top-0 z-30 flex items-center justify-between px-4 lg:px-10 py-3 border-b border-white/5 bg-[#090D14]/70 backdrop-blur">
          <div className="flex items-center gap-2 lg:hidden">
            <Zap className="w-5 h-5 text-amber-400" />
            <span className="font-bold text-sm">AVES</span>
          </div>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="relative" data-testid="notifications-btn">
                  <Bell className="w-4 h-4" />
                  {unread > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-[#090D14]" />
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-96 bg-[#0B111C] border-white/10 p-0" data-testid="notifications-panel">
                <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                  <div>
                    <div className="font-semibold text-sm">Bildirimler</div>
                    <div className="text-[11px] text-slate-500">{unread} okunmamış</div>
                  </div>
                  {unread > 0 && (
                    <Button size="sm" variant="ghost" onClick={markAllRead} className="text-xs" data-testid="mark-all-read-btn">
                      <CheckCheck className="w-3.5 h-3.5 mr-1" /> Tümü okundu
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <div className="p-8 text-center text-slate-500 text-sm">Henüz bildirim yok</div>
                  ) : (
                    <ul className="divide-y divide-white/5">
                      {notifs.slice(0, 20).map((n) => (
                        <li key={n.id} className={`px-4 py-3 text-sm ${!n.read ? "bg-amber-500/5" : ""}`}>
                          <div className="flex items-center gap-2">
                            {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />}
                            <div className="font-medium">{n.title}</div>
                          </div>
                          {n.message && <div className="text-slate-400 text-xs mt-1">{n.message}</div>}
                          <div className="text-[10px] mono text-slate-500 mt-1">{new Date(n.created_at).toLocaleString("tr-TR")}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </PopoverContent>
            </Popover>
            <Button size="sm" variant="ghost" onClick={doLogout} className="lg:hidden" data-testid="logout-btn-mobile">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
        <div className="p-6 lg:p-10 max-w-[1500px] mx-auto fade-in">{children}</div>
      </main>
    </div>
  );
}
